import { baseApiClient } from 'api/BaseApiClient';
import { parseSSEStream } from 'entities/chat/api/sseParser';
import type { ChatStreamEvent } from 'entities/chat/types';

const CHAT_URL = '/v1/chat/';
const CONFIRM_URL = '/v1/chat/confirm/';
const THREAD_URL = '/v1/chat/thread/';

function messageForStatus(status: number): string {
  switch (status) {
    case 401:
      return 'Your session expired. Please sign in again.';
    case 403:
      return "You don't have access to the assistant.";
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 503:
      return 'The assistant is currently unavailable.';
    default:
      return 'Something went wrong handling your request.';
  }
}

// What the user was looking at when they sent the message, forwarded to the assistant + docs lookup.
export type ChatContext = {
  path?: string;
  sandbox?: boolean;
};

// Open one streaming chat turn. Emits typed events until the stream finishes, errors, or aborts.
// threadId continues an existing conversation; omit it to start a new one.
export async function streamChat(
  message: string,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
  threadId?: string | null,
  context?: ChatContext,
): Promise<void> {
  let response: Response;
  try {
    response = await baseApiClient.stream(CHAT_URL, { message, thread_id: threadId ?? undefined, context }, signal);
  } catch {
    onEvent(signal?.aborted ? { type: 'abort' } : { type: 'error', message: 'Could not reach the assistant.' });
    return;
  }

  if (!response.ok) {
    onEvent({ type: 'error', message: messageForStatus(response.status) });
    return;
  }

  try {
    await parseSSEStream(response, onEvent);
  } catch {
    onEvent(signal?.aborted ? { type: 'abort' } : { type: 'error', message: 'The connection was interrupted.' });
  }
}

export type ConfirmOrderResult = { ok: true; orderGroupId: string; env: string } | { ok: false; message: string };

// Place a previewed order — the only write path, user-initiated, gated server-side by
// DASHBOARD_ASSISTANT_WRITES_ENABLED. idempotencyKey must be stable across retries of the same
// preview; threadId lets the server record the placed order on the thread.
export async function confirmOrder(
  previewToken: string,
  idempotencyKey: string,
  threadId?: string | null,
): Promise<ConfirmOrderResult> {
  try {
    const data = await baseApiClient.post<{ order_group_id: string; env: string }>(CONFIRM_URL, {
      preview_token: previewToken,
      idempotency_key: idempotencyKey,
      thread_id: threadId ?? undefined,
    });
    return { ok: true, orderGroupId: data.order_group_id, env: data.env };
  } catch (error) {
    // The API client can throw any parsed JSON value (incl. null/primitive), so narrow before reading.
    const message =
      typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
        ? error.message
        : 'Could not place the order.';
    return { ok: false, message };
  }
}

export type ServerMessage = {
  id: string;
  role: string;
  content: string;
  tool_calls?: { id: string; tool: string }[];
  order_group_id?: string | null;
  created_at: string;
};

export type ThreadPage = {
  threadId: string | null;
  messages: ServerMessage[];
  hasMore: boolean;
};

// One page of a thread's messages, chronological (oldest first). No args -> the latest thread's
// most recent page. `beforeId` (the oldest message the caller already has) pages older messages,
// for the panel's scroll-to-top loader. Throws on error so the caller's query can retry/surface it.
export async function getThreadMessages(params?: {
  threadId?: string | null;
  beforeId?: string | null;
}): Promise<ThreadPage> {
  const search = new URLSearchParams();
  if (params?.threadId) {
    search.set('thread_id', params.threadId);
  }
  if (params?.beforeId) {
    search.set('before_id', params.beforeId);
  }
  const query = search.toString();
  const data = await baseApiClient.get<{ thread_id: string | null; messages: ServerMessage[]; has_more?: boolean }>(
    query ? `${THREAD_URL}?${query}` : THREAD_URL,
  );
  return { threadId: data.thread_id, messages: data.messages ?? [], hasMore: Boolean(data.has_more) };
}
